import { useEffect } from 'react'
import { matchPath, NavLink, useLocation, useNavigate } from 'react-router-dom'
import { SidebarConfigProps } from '@/reducers/sidebar.slice'

import { mtc, TextColors, TypographyStyles } from '@/styles'
import { Accordion, Badge, Icon, List, Text } from '@/ui'
import {
  pageItemStyles,
  pageListItemStyles,
  pageDivItemStyles,
  pageAccordionTriggerStyles,
} from './styles'

export interface NavListProps {
  categories?: Record<string, SidebarConfigProps> | null
  className?: string
  disabled?: boolean
  selectedCategory?: string | null
}

export const NavList = ({
  categories,
  className,
  disabled = false,
  selectedCategory,
  ...rest
}: NavListProps) => {
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    if (!selectedCategory || !categories) return

    const selected = categories[selectedCategory]
    const firstPage = selected?.pages?.[0]

    if (firstPage?.to && location.pathname !== firstPage.to) {
      navigate(firstPage.to)
    }
  }, [selectedCategory, categories, navigate, location.pathname])

  if (!categories) {
    return null
  }

  const activeAccordionKeys = Object.entries(categories).reduce<string[]>(
    (acc, [key, category]) => {
      if (!category.pages) return acc

      const hasActivePage = category.pages.some(
        (page) =>
          page.to && matchPath({ path: page.to, end: false }, location.pathname)
      )

      if (hasActivePage) {
        acc.push(key)
      }
      if (selectedCategory === key && categories?.[selectedCategory]?.pages) {
        acc.push(key)
      }

      return acc
    },
    []
  )

  return (
    <Accordion
      type="multiple"
      defaultValue={activeAccordionKeys.length > 0 ? activeAccordionKeys : []}
    >
      {Object.entries(categories).map(([key, category]) => {
        // const isSelected = selectedCategory === key
        const isActive = Boolean(
          category.to &&
            matchPath({ path: category.to, end: false }, location.pathname)
        )
        if (category.pages) {
          return (
            <Accordion.Item
              key={key}
              value={key}
              className={mtc(pageItemStyles({ disabled }))}
              {...rest}
            >
              <Accordion.Trigger
                className={mtc(pageAccordionTriggerStyles({ isActive }))}
              >
                <div className="flex items-center">
                  {category.icon && <Icon icon={category.icon} size="1x" />}
                  <Text variant={TypographyStyles.H3}>{category.label}</Text>
                </div>
              </Accordion.Trigger>

              <Accordion.Content className={className}>
                <List>
                  {category.pages.map(
                    ({ label, to, end, isNewFeature, icon }) => {
                      const isActive = Boolean(
                        to &&
                          matchPath({ path: to, end: false }, location.pathname)
                      )
                      return (
                        <List.Item
                          key={label}
                          className={mtc(pageListItemStyles({ isActive }))}
                        >
                          <NavLink
                            end={end}
                            to={to ?? ''}
                            className="flex items-center w-full"
                          >
                            <Text
                              variant={TypographyStyles.H3}
                              className="flex items-center gap-1 ml-2"
                            >
                              {label}
                              {icon && <Icon icon={icon} size="1x" />}
                            </Text>
                            {isNewFeature && (
                              <Badge color="yellow" className="ml-4" size="sm">
                                <Text
                                  variant={TypographyStyles.EYEBROW}
                                  color={TextColors.WHITE}
                                >
                                  NEW
                                </Text>
                              </Badge>
                            )}
                          </NavLink>
                        </List.Item>
                      )
                    }
                  )}
                </List>
              </Accordion.Content>
            </Accordion.Item>
          )
        } else {
          return (
            <div key={key} className={mtc(pageDivItemStyles())}>
              <NavLink
                key={key}
                end={category.end}
                to={category.to ?? ''}
                className="w-full"
              >
                <div className="flex items-center">
                  {category.icon && <Icon icon={category.icon} size="1x" />}
                  <Text variant={TypographyStyles.H3}>{category.label}</Text>
                </div>
              </NavLink>
            </div>
          )
        }
      })}
    </Accordion>
  )
}
